if (!customElements.get('yt-map-shop')) {
  const parseTiers = (value) =>
    String(value || '')
      .split(',')
      .map((pair) => pair.split(':').map((part) => Number(part.trim())))
      .filter(([min, pct]) => min > 0 && pct > 0)
      .map(([min, pct]) => ({ min, pct }))
      .sort((a, b) => a.min - b.min);

  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value), template || '');

  class YtMapShop extends HTMLElement {
    connectedCallback() {
      this.strings = JSON.parse(this.querySelector('[data-yt-strings]')?.textContent || '{}');
      this.preview = this.dataset.discountPreview === 'true';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.cartVariantQty = new Map();

      this.money = this.moneyFormatter(this.dataset.moneySample || '');

      this.panels = [...this.querySelectorAll('[data-yt-panel]')].map((el) => this.setupPanel(el));
      this.active = this.panels.find((panel) => !panel.el.hidden) || this.panels[0];

      this.querySelectorAll('[data-yt-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.dataset.ytTab));
      });
      document.addEventListener('click', (event) => {
        const jump = event.target.closest('[data-yt-game-jump]');
        if (jump) this.select(jump.dataset.ytGameJump);
      });

      this.querySelectorAll('[data-yt-reviews]').forEach((el) => this.setupReviews(el));
      this.setupDock();
      this.panels.forEach((panel) => this.render(panel));
      this.refreshCart();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'yt-map-shop') this.refreshCart();
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.observer?.disconnect();
    }

    setupPanel(el) {
      const panel = {
        el,
        key: el.dataset.ytPanel,
        tiers: this.preview ? parseTiers(el.dataset.tiers) : [],
        qty: new Map(),
        products: new Map(),
        variants: new Map(),
        summary: el.querySelector('[data-yt-summary]'),
      };
      const slot = el.querySelector('[data-yt-express-slot]');

      el.querySelectorAll('[data-yt-product]').forEach((productEl) => {
        const product = {
          id: Number(productEl.dataset.ytProduct),
          el: productEl,
          sizeIndex: Number(productEl.dataset.sizeIndex),
          mapIndex: Number(productEl.dataset.mapIndex),
          variants: JSON.parse(productEl.querySelector('[data-yt-variants]').textContent),
          size: productEl.querySelector('[data-yt-size]:checked')?.value,
          fallbackName: productEl.querySelector('[data-yt-card]')?.dataset.map || '',
          express: productEl.querySelector('[data-yt-express]'),
        };
        product.variants.forEach((variant) => panel.variants.set(variant.id, { ...variant, product }));
        panel.products.set(product.id, product);
        if (product.express && slot) slot.append(product.express);

        this.markSize(product);
        productEl.addEventListener('change', (event) => {
          if (!event.target.matches('[data-yt-size]')) return;
          product.size = event.target.value;
          product.variants.forEach((variant) => panel.qty.delete(variant.id));
          this.markSize(product);
          this.syncCards(panel, product, true);
          this.render(panel);
        });
      });

      panel.products.forEach((product) => this.syncCards(panel, product));

      el.addEventListener('click', (event) => {
        const scroll = event.target.closest('[data-yt-scroll]');
        if (scroll) {
          const grid = scroll.parentElement.querySelector('.yt-shop__grid');
          grid?.scrollBy({ left: Number(scroll.dataset.ytScroll) * Math.max(280, grid.clientWidth * 0.8), behavior: 'smooth' });
          return;
        }
        const step = event.target.closest('[data-yt-step]');
        if (step) {
          const card = step.closest('[data-yt-card]');
          this.change(panel, Number(card.dataset.variant), Number(step.dataset.ytStep));
          return;
        }
        const remove = event.target.closest('[data-yt-remove]');
        if (remove) this.change(panel, Number(remove.dataset.ytRemove), -Infinity);
      });

      panel.summary?.querySelector('[data-yt-add]')?.addEventListener('click', () => this.addToCart(panel));
      panel.summary?.querySelector('[data-yt-buy]')?.addEventListener('click', () => this.addToCart(panel, true));
      return panel;
    }

    setupReviews(el) {
      const track = el.querySelector('[data-yt-reviews-track]');
      const dots = [...el.querySelectorAll('[data-yt-reviews-dots] button')];
      if (!track || !dots.length) return;
      const cards = [...track.children];
      const current = () => Math.round(track.scrollLeft / Math.max(1, cards[0].offsetWidth));
      const go = (index) => {
        const n = (index + cards.length) % cards.length;
        track.scrollTo({ left: cards[n].offsetLeft - cards[0].offsetLeft, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      };
      dots.forEach((dot, n) => dot.addEventListener('click', () => go(n)));
      el.querySelectorAll('[data-yt-reviews-step]').forEach((button) =>
        button.addEventListener('click', () => go(current() + Number(button.dataset.ytReviewsStep)))
      );
      let frame = 0;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const n = current();
          dots.forEach((dot, i) => (i === n ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current')));
        });
      }, { passive: true });
    }

    select(key) {
      const panel = this.panels.find((item) => item.key === key);
      if (!panel) return;
      this.active = panel;
      this.panels.forEach((item) => (item.el.hidden = item !== panel));
      this.querySelectorAll('[data-yt-tab]').forEach((tab) =>
        tab.setAttribute('aria-selected', String(tab.dataset.ytTab === key))
      );
      this.observeSummary();
      this.updateDock();
    }

    markSize(product) {
      const model = product.el.querySelector('[data-yt-compare]');
      if (!model) return;
      const caption = model.querySelector('[data-yt-compare-caption]');
      if (!product.labels) {
        product.labels = new Map(
          [...caption.querySelectorAll('template')].map((tpl) => [tpl.dataset.ytCompareLabel, tpl.content.textContent.trim()])
        );
        product.caption = caption.textContent.trim();
      }
      const key = String(product.size || '').toUpperCase().includes('A3') ? 'A3' : 'A4';
      if (model.dataset.size === key) return;
      const grow = key === 'A3';
      model.classList.toggle('is-growing', grow);
      model.classList.toggle('is-shrinking', !grow);
      model.dataset.size = key;

      const next = product.labels.get(product.size) || key;
      this.roll(caption, product.caption, next, grow);
      product.caption = next;
    }

    roll(el, from, to, up) {
      clearTimeout(el.rollTimer);
      if (this.reduceMotion) {
        el.textContent = to;
        return;
      }
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      sr.textContent = to;
      const shown = document.createElement('span');
      shown.setAttribute('aria-hidden', 'true');
      const length = Math.max(from.length, to.length);
      let changed = 0;
      for (let i = 0; i < length; i++) {
        const a = from[i] || ' ';
        const b = to[i] || ' ';
        if (a === b) {
          shown.append(a);
          continue;
        }
        const slot = document.createElement('span');
        slot.className = 'yt-roll';
        const reel = document.createElement('span');
        reel.className = up ? 'yt-roll__reel' : 'yt-roll__reel is-down';
        reel.style.transitionDelay = `${changed++ * 35}ms`;
        const old = document.createElement('span');
        old.textContent = a;
        const fresh = document.createElement('span');
        fresh.textContent = b;
        if (up) reel.append(old, fresh);
        else reel.append(fresh, old);
        slot.append(reel);
        shown.append(slot);
      }
      el.classList.remove('is-rolling');
      el.replaceChildren(sr, shown);
      requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-rolling')));
      el.rollTimer = setTimeout(() => {
        el.classList.remove('is-rolling');
        el.textContent = to;
      }, 700 + changed * 35);
    }

    variantFor(product, card) {
      if (product.mapIndex < 0) return product.variants.find((variant) => variant.id === Number(card.dataset.variant));
      return product.variants.find(
        (variant) =>
          variant.options[product.mapIndex] === card.dataset.map &&
          (product.sizeIndex < 0 || variant.options[product.sizeIndex] === product.size)
      );
    }

    syncCards(panel, product, swap = false) {
      product.el.querySelectorAll('[data-yt-card]').forEach((card) => {
        const variant = this.variantFor(product, card);
        if (!variant) return;
        const qty = panel.qty.get(variant.id) || 0;
        card.dataset.variant = variant.id;
        card.querySelectorAll('[data-yt-card-link]').forEach((link) => {
          const url = new URL(link.href, location.origin);
          url.searchParams.set('variant', variant.id);
          link.href = url.pathname + url.search;
        });
        card.classList.toggle('is-picked', qty > 0);
        card.classList.toggle('is-sold-out', !variant.available);
        card.querySelector('[data-yt-qty]').textContent = qty;
        card.querySelector('[data-yt-step="-1"]').disabled = qty === 0;
        card.querySelector('[data-yt-step="1"]').disabled = !variant.available;
        card.querySelector('[data-yt-card-price]').textContent = this.format(variant.price);

        const badge = card.querySelector('[data-yt-card-badge]');
        badge.hidden = variant.available;
        badge.textContent = this.strings.soldOut;

        const img = card.querySelector('img');
        if (!swap || !img || !variant.image || img.dataset.current === variant.image) return;
        const apply = () => {
          img.removeAttribute('srcset');
          img.src = variant.image;
          img.dataset.current = variant.image;
          img.classList.remove('is-swapping');
        };
        if (this.reduceMotion) return apply();
        img.classList.add('is-swapping');
        setTimeout(apply, 180);
      });
    }

    change(panel, variantId, delta) {
      const variant = panel.variants.get(variantId);
      if (!variant) return;
      const next = Math.max(0, (panel.qty.get(variantId) || 0) + delta);
      if (next === 0) panel.qty.delete(variantId);
      else panel.qty.set(variantId, next);
      this.syncCards(panel, variant.product);
      this.render(panel);
    }

    lineName(variant) {
      const { product } = variant;
      const name = product.mapIndex >= 0 ? variant.options[product.mapIndex] : product.fallbackName;
      const size = product.sizeIndex >= 0 ? variant.options[product.sizeIndex] : '';
      return { name, size };
    }

    cartQty(product) {
      return product.variants.reduce((sum, variant) => {
        if (product.sizeIndex >= 0 && variant.options[product.sizeIndex] !== product.size) return sum;
        return sum + (this.cartVariantQty.get(variant.id) || 0);
      }, 0);
    }

    totals(panel) {
      const lines = [...panel.qty].map(([id, qty]) => ({ variant: panel.variants.get(id), qty }));
      const count = lines.reduce((sum, line) => sum + line.qty, 0);
      const inCart = panel.tiers.length
        ? [...panel.products.values()].reduce((sum, product) => sum + this.cartQty(product), 0)
        : 0;
      const tierCount = count + inCart;
      const reached = panel.tiers.filter((tier) => tierCount >= tier.min).pop();
      const pct = reached ? reached.pct : 0;
      const subtotal = lines.reduce((sum, line) => sum + line.variant.price * line.qty, 0);
      const discount = lines.reduce((sum, line) => sum + Math.round((line.variant.price * line.qty * pct) / 100), 0);
      return { lines, count, inCart, tierCount, pct, subtotal, discount, total: subtotal - discount };
    }

    render(panel) {
      const summary = panel.summary;
      if (!summary) return;
      const t = this.totals(panel);
      panel.last = t;

      summary.querySelector('[data-yt-count]').textContent = t.count ? fill(this.strings.items, { count: t.count }) : '';
      summary.querySelector('[data-yt-empty]').hidden = t.count > 0;

      const list = summary.querySelector('[data-yt-lines]');
      list.replaceChildren(
        ...t.lines.map(({ variant, qty }) => {
          const { name, size } = this.lineName(variant);
          const li = document.createElement('li');
          const label = document.createElement('span');
          label.textContent = name;
          if (size) {
            const small = document.createElement('small');
            small.textContent = size;
            label.append(' ', small);
          }
          const amount = document.createElement('span');
          amount.className = 'yt-muted';
          amount.textContent = `${qty} ×`;
          const price = document.createElement('b');
          price.textContent = this.format(variant.price * qty);
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.dataset.ytRemove = variant.id;
          remove.setAttribute('aria-label', `${name} ${size}`.trim());
          remove.textContent = '×';
          li.append(label, amount, price, remove);
          return li;
        })
      );

      summary.querySelector('[data-yt-subtotal]').textContent = this.format(t.subtotal);
      summary.querySelector('[data-yt-total]').textContent = this.format(t.total);
      const discountRow = summary.querySelector('[data-yt-discount-row]');
      discountRow.hidden = !(t.pct > 0 && t.count > 0);
      summary.querySelector('[data-yt-discount-pct]').textContent = `−${t.pct} %`;
      summary.querySelector('[data-yt-discount]').textContent = `−${this.format(t.discount)}`;

      const tiers = summary.querySelector('[data-yt-tiers]');
      if (tiers && panel.tiers.length) {
        const top = panel.tiers[panel.tiers.length - 1].min;
        tiers.style.setProperty('--yt-progress', Math.min(1, t.tierCount / top));
        tiers.querySelectorAll('[data-tier]').forEach((li) => {
          li.classList.toggle('is-reached', t.tierCount >= Number(li.dataset.tier));
        });
        const next = panel.tiers.find((tier) => t.tierCount < tier.min);
        summary.querySelector('[data-yt-nudge]').textContent = next
          ? fill(this.strings.nextTier, { count: next.min - t.tierCount, percent: next.pct })
          : fill(this.strings.topTier, { percent: t.pct });
      }

      const inCart = summary.querySelector('[data-yt-in-cart]');
      inCart.hidden = t.inCart === 0;
      inCart.textContent = fill(this.strings.inCart, { count: t.inCart });

      summary.querySelector('[data-yt-add]').disabled = t.count === 0;
      summary.querySelector('[data-yt-buy]').disabled = t.count === 0;

      const single = t.lines.length === 1 ? t.lines[0] : null;
      panel.products.forEach((product) => {
        if (!product.express) return;
        const show = single && single.variant.product === product;
        product.express.hidden = !show;
        if (!show) return;
        product.express.querySelector('input[name="id"]').value = single.variant.id;
        product.express.querySelector('input[name="quantity"]').value = single.qty;
      });

      if (panel === this.active) this.updateDock();
    }

    moneyFormatter(sample) {
      const withCents = sample.match(/1([^\d]?)234([^\d])56/);
      const noCents = withCents ? null : sample.match(/1([^\d]?)235/);
      const match = withCents || noCents;
      if (!match) return (cents) => (cents / 100).toFixed(2);
      const prefix = sample.slice(0, match.index);
      const suffix = sample.slice(match.index + match[0].length);
      const group = match[1];
      return (cents) => {
        const whole = withCents ? Math.floor(cents / 100) : Math.round(cents / 100);
        const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, group);
        const fraction = withCents ? `${match[2]}${String(cents % 100).padStart(2, '0')}` : '';
        return `${prefix}${grouped}${fraction}${suffix}`;
      };
    }

    format(cents) {
      return this.money(Math.round(cents));
    }

    refreshCart() {
      const url = `${window.routes?.cart_url || '/cart'}.js`;
      fetch(url, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          this.cartVariantQty = (cart.items || []).reduce((counts, item) => {
            counts.set(item.variant_id, (counts.get(item.variant_id) || 0) + item.quantity);
            return counts;
          }, new Map());
          this.panels.forEach((panel) => this.render(panel));
        })
        .catch(() => {});
    }

    setBusy(panel, busy) {
      [panel.summary?.querySelector('[data-yt-add]'), panel.summary?.querySelector('[data-yt-buy]'), this.dockAdd]
        .filter(Boolean)
        .forEach((button) => {
          button.classList.toggle('is-loading', busy);
          button.setAttribute('aria-busy', String(busy));
          if (busy) button.disabled = true;
        });
      if (!busy) this.render(panel);
    }

    addToCart(panel, buyNow = false) {
      const items = [...panel.qty].map(([id, quantity]) => ({ id, quantity }));
      if (!items.length) return;
      const error = panel.summary.querySelector('[data-yt-error]');
      error.hidden = true;

      const drawer = document.querySelector('cart-drawer');
      const body = { items };
      if (!buyNow && drawer?.getSectionsToRender) {
        body.sections = drawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      this.setBusy(panel, true);
      fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(data.description || data.message || this.strings.error);
          if (buyNow) {
            window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
            return;
          }
          panel.qty.clear();
          panel.products.forEach((product) => this.syncCards(panel, product));
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'yt-map-shop', cartData: data });
          }
          if (drawer?.renderContents) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(data);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
          this.refreshCart();
        })
        .catch((err) => {
          error.textContent = err.message || this.strings.error;
          error.hidden = false;
        })
        .finally(() => this.setBusy(panel, false));
    }

    setupDock() {
      this.dock = this.querySelector('[data-yt-dock]');
      if (!this.dock) return;
      this.dockAdd = this.dock.querySelector('[data-yt-dock-add]');
      this.dockAdd.addEventListener('click', () => this.addToCart(this.active));
      this.inView = false;
      this.summaryVisible = false;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this) this.inView = entry.isIntersecting;
          else if (entry.target === this.observedSummary) this.summaryVisible = entry.isIntersecting;
        });
        this.updateDock();
      });
      this.observer.observe(this);
      this.observeSummary();
    }

    observeSummary() {
      if (!this.observer) return;
      if (this.observedSummary) this.observer.unobserve(this.observedSummary);
      this.observedSummary = this.active?.summary?.querySelector('.yt-shop__actions');
      this.summaryVisible = false;
      if (this.observedSummary) this.observer.observe(this.observedSummary);
    }

    updateDock() {
      if (!this.dock || !this.active?.last) return;
      const t = this.active.last;
      this.dock.hidden = !(this.inView && !this.summaryVisible && t.count > 0);
      this.dock.querySelector('[data-yt-dock-count]').textContent = fill(this.strings.items, { count: t.count });
      this.dock.querySelector('[data-yt-dock-total]').textContent = this.format(t.total);
      if (!this.dockAdd.classList.contains('is-loading')) this.dockAdd.disabled = t.count === 0;
    }
  }

  customElements.define('yt-map-shop', YtMapShop);
}
